import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { useDebug } from '@/utils/useDebugLog';

import className from "classnames/bind";
import styles from "./ImageAnim.module.scss";
const cx = className.bind(styles);

type ImageAnimProps = {
  index?: number; // for debugging
  width?: number | string;
  height?: number | string;
  src: string;
  alt?: string;
  isAnimEnabled?: boolean;
  scalesOnHover?: number[]; // scales used when hovering/contracted by hover
  scalesOnStart?: number[]; // initial scales used for reveal animation
};

export type ImageAnimHandle = {
  contract: () => void;
  reverse: () => void;
};

const ImageAnim = forwardRef(function ImageAnim({
  index,
  width,
  height,
  src,
  alt = '',
  // isAnimEnabled = true,
  scalesOnHover,
  scalesOnStart
}: ImageAnimProps, ref) {

  const { log } = useDebug({ name: 'ImageAnim', bgColor: 'pink', enabled: false }); // index === 0 });

  // Element refs
  const rootEl = useRef<HTMLDivElement | null>(null);
  const dupeRefs = useRef<Array<HTMLImageElement | null>>([]);

  const duplicateCount = Array.isArray(scalesOnHover) ? scalesOnHover.length : 0;

  // -------------------- Initial setup

  const [hasAnimatedIn, setHasAnimatedIn] = useState(false);

  // TODO: On first render, if there are scalesOnStart, set each duplicate to its start scale
  useEffect(() => {
    if (duplicateCount > 0 && Array.isArray(scalesOnStart) && !hasAnimatedIn) {
      log('initial setup');
      setHasAnimatedIn(true);
      dupeRefs.current.forEach((el, i) => {
        if (!el) return;
        const startScale = scalesOnStart[i] || 1;
        el.style.setProperty('--scale-current', String(startScale));
      });
    }
  }, [duplicateCount, scalesOnStart]);

  // -------------------- Contract / Reverse

  useImperativeHandle(ref, (duration: number = 0.35) => ({
    contract: () => {
      log('contract');
      // Set the parent transition duration that the individual duplicates will inherit
      rootEl.current?.style.setProperty('--anim-duration', `${duration}s`);

      // Set each duplicate's --scale-current to respective hover array value
      dupeRefs.current.forEach((el, i) => {
        if (!el) return;
        el.style.setProperty('--scale-current', `${scalesOnHover[i]}`);
      });
    },
    reverse: (duration: number = 0.35) => {
      log('reverse');
      // Set the parent transition duration that the individual duplicates will inherit
      rootEl.current?.style.setProperty('--anim-duration', `${duration}s`);

      // Set each duplicate's --scale-current back to 1 to reverse the animation
      dupeRefs.current.forEach((el) => {
        if (!el) return;
        el.style.setProperty('--scale-current', '1');
      });
    },
  }), [scalesOnHover]);

  // -------------------- Render

  return (
    <div className={cx(['image-anim'])} ref={rootEl}>

      {/* Base image */}
      <img className={cx('image-anim__base')}
        src={src}
        alt={alt}
        width={width}
        height={height}
      />

      {/* Duplicate images for layered animation */}
      {duplicateCount > 0 && (
        <>
          {Array.from({ length: duplicateCount }).map((_, i) => {
            return (
              <img className={cx('image-anim__dupe')}
                key={`dup-${i}`}
                ref={(el) => { dupeRefs.current[i] = el }}
                src={src}
                alt=""
                aria-hidden="true"
                width={width}
                height={height}
              />
            );
          })}
        </>
      )}
    </div>
  );

});

export default ImageAnim;
